'use client'

import { isActivePathname, titleCaseToCamelCase } from '@cloud/shared/ui'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from '@tanstack/react-router'
import { useLocale, useLocaleDirection, useTranslations } from 'gt-react'
import { Box, Building2, Check, ChevronDown, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { DynamicIcon } from '@/components/dynamic-icon'
import type { DatasetMenuItem } from '@/components/integrations/dataset-menu'
import { groupHostsModule, mergeDatasetItems } from '@/components/integrations/dataset-menu'
import { useDatasetMenu } from '@/components/integrations/use-dataset-menu'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarSeparator,
  Sidebar as SidebarWrapper,
  useSidebar,
} from '@/components/ui/sidebar'
import { moduleNavigationsData } from '@/data/module-navigations'
import { useHiddenSidebarModules } from '@/hooks/use-hidden-sidebar-modules'
import { BACKEND_URL, orpc } from '@/lib/orpcClient'
import { useModulesContext } from '@/providers/modules-provider'
import { useUser } from '@/providers/user-provider'
import type { NavigationNestedItem, NavigationRootItem } from '@/types'
import { CommandMenu } from './command-menu'

export function Sidebar() {
  const t = useTranslations()
  const pathname = useLocation()
  const { openMobile, setOpenMobile } = useSidebar()
  const locale = useLocale()
  const direction = useLocaleDirection(locale) // e.g. "rtl" for "right-to-left"
  const isRTL = direction === 'rtl'
  const { current } = useUser()
  const { isModuleEnabled, isFeatureEnabled, isLoading: modulesLoading } = useModulesContext()
  const { isHidden: isModuleHiddenInSidebar } = useHiddenSidebarModules()

  // Workspace switcher: list all workspaces the current user has
  // access to (from the cross-tenant erp_user_tenants index). If the
  // user is in more than one workspace, the sidebar shows a switcher
  // dropdown at the top.
  const { data: workspaceData } = useQuery({
    ...orpc.user.workspaces.queryOptions(),
    retry: false,
    throwOnError: false,
  })

  const { data: companySettings } = useQuery({
    ...orpc.company.settings.get.queryOptions(),
    retry: false,
    throwOnError: false,
  })

  // Accordion: at most one sidebar group is open at a time. Persisted in
  // localStorage so the open group survives page navigations.
  const [openGroup, setOpenGroup] = useState<string | null>(() => {
    try {
      return localStorage.getItem('sidebar-open-group')
    } catch {
      return null
    }
  })

  // Every group starts collapsed; only the single `openGroup` is expanded.
  const isGroupCollapsed = (key: string) => openGroup !== key

  const toggleGroup = useCallback((key: string) => {
    setOpenGroup((prev) => {
      const next = prev === key ? null : key
      try {
        if (next) localStorage.setItem('sidebar-open-group', next)
        else localStorage.removeItem('sidebar-open-group')
      } catch {}
      return next
    })
  }, [])

  // Settings joins the one-open-at-a-time accordion: while you're in the
  // settings area, collapse any open module group so Settings reads as the
  // active section. Clicking a module group header re-opens it via toggleGroup.
  useEffect(() => {
    if (pathname.pathname.startsWith('/settings')) {
      setOpenGroup('Settings')
      try {
        localStorage.setItem('sidebar-open-group', 'Settings')
      } catch {}
    }
  }, [pathname.pathname])

  // Custom modules are loaded directly (not through the static registry)
  // so the sidebar shows anything a tenant has built or installed from
  // the marketplace. Silently fails on pre-migration tenants or users
  // with no custom-module grants.
  // De lijstprocedure pagineert serverzijde; de zijbalk wil ze allemaal, dus hij
  // vraagt expliciet om het plafond (LIST_MAX_LIMIT) in plaats van stilzwijgend op
  // de standaardpagina van 200 te blijven steken.
  const customModulesQ = useQuery({
    ...orpc.modules.custom.modules.list.queryOptions({ input: { limit: 500 } }),
    retry: false,
    throwOnError: false,
  })
  const customModules = customModulesQ.data?.rows

  // Nav items for every module in one batch call — avoids N+1. Used to
  // show collapsible sub-entries beneath each custom module.
  const { data: navGroups } = useQuery({
    ...orpc.modules.custom.nav.listForSidebar.queryOptions(),
    retry: false,
    throwOnError: false,
  })
  type SidebarNavItem = NonNullable<typeof navGroups>[number]['items'][number]
  const navItemsByModule = new Map<string, SidebarNavItem[]>()
  for (const group of navGroups ?? []) {
    navItemsByModule.set(group.moduleKey, group.items)
  }

  // External (connected-service) modules — separate from no-code custom
  // modules. The manifest is cached on the connection row; we read its
  // nav array straight from the cached JSON.
  const { data: externalConnections } = useQuery({
    ...orpc.external.connections.list.queryOptions(),
    retry: false,
    throwOnError: false,
  })

  const { data: hasEmployee } = useQuery({
    ...orpc.hr.me.hasOwnEmployee.queryOptions(),
    enabled: isModuleEnabled('hr'),
    retry: false,
    throwOnError: false,
  })

  // Datasets met een menuplek: ze komen in de groep van hun module (spec §9.2).
  const datasets = useDatasetMenu()

  // Helper to check if a nav item should be shown based on module status
  // and the user's "hidden in sidebar" preference.
  const shouldShowNavItem = (item: NavigationRootItem | NavigationNestedItem): boolean => {
    // If no moduleKey, always show
    if (!('moduleKey' in item) || !item.moduleKey) {
      return true
    }
    // The module must be enabled tenant-wide AND not hidden by this user.
    if (!isModuleEnabled(item.moduleKey)) return false
    if (isModuleHiddenInSidebar(item.moduleKey)) return false
    if (item.featureKey && !isFeatureEnabled(item.moduleKey, item.featureKey)) return false
    return true
  }

  // Filter navigation items based on module status
  const filterNavItems = <T extends NavigationRootItem | NavigationNestedItem>(items: T[]): T[] => {
    return items.filter(shouldShowNavItem)
  }

  // Een dataset draagt zijn titel al vertaald (uit het label van de taak) — geen
  // `navigation.*`-sleutel zoals de vaste items.
  const renderDatasetItem = (item: DatasetMenuItem) => (
    <SidebarMenuButton
      isActive={isActivePathname(item.href, pathname.pathname)}
      onClick={() => setOpenMobile(!openMobile)}
      asChild
    >
      <Link to={item.href}>
        <DynamicIcon name={item.iconName} className="h-4 w-4" />
        <span>{item.title}</span>
      </Link>
    </SidebarMenuButton>
  )

  const renderMenuItem = (item: NavigationRootItem | NavigationNestedItem) => {
    const title = t(`navigation.${titleCaseToCamelCase(item.title)}`)
    const label = item.label && t(`label.${titleCaseToCamelCase(item.label)}`)

    // If the item has nested items, render it with a collapsible dropdown.
    if (item.items) {
      return (
        <Collapsible className="group/collapsible">
          <CollapsibleTrigger asChild>
            <SidebarMenuButton className="w-full justify-between [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center">
                {'iconName' in item && (
                  <DynamicIcon name={item.iconName} className="me-2 h-4 w-4" />
                )}
                <span>{title}</span>
                {'label' in item && (
                  <Badge variant="secondary" className="me-2">
                    {label}
                  </Badge>
                )}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
            <SidebarMenuSub>
              {item.items.map((subItem: NavigationNestedItem) => (
                <SidebarMenuItem key={subItem.title}>{renderMenuItem(subItem)}</SidebarMenuItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </Collapsible>
      )
    }

    // Otherwise, render the item with a link.
    if ('href' in item) {
      const localizedPathname = item.href
      const isActive = isActivePathname(localizedPathname, pathname.pathname)

      return (
        <SidebarMenuButton isActive={isActive} onClick={() => setOpenMobile(!openMobile)} asChild>
          <Link to={localizedPathname}>
            {'iconName' in item && <DynamicIcon name={item.iconName} className="h-4 w-4" />}
            <span>{title}</span>
            {'label' in item && <Badge variant="secondary">{label}</Badge>}
          </Link>
        </SidebarMenuButton>
      )
    }
  }

  return (
    <SidebarWrapper side={isRTL ? 'right' : 'left'}>
      <SidebarHeader>
        <WorkspaceHeader
          logoUrl={companySettings?.logoVignetUrl ?? null}
          displayName={
            companySettings?.tradingName ||
            companySettings?.legalName ||
            workspaceData?.current.name ||
            current?.org.name ||
            t('navigation.workspace')
          }
          current={workspaceData?.current}
          workspaces={workspaceData?.workspaces ?? []}
        />
        <CommandMenu buttonClassName="max-w-full" />
      </SidebarHeader>
      <ScrollArea>
        <SidebarContent className="gap-0">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={isActivePathname('/dashboard', pathname.pathname)}
                    onClick={() => setOpenMobile(!openMobile)}
                    asChild
                  >
                    <Link to="/dashboard">
                      <DynamicIcon name="LayoutDashboard" className="h-4 w-4" />
                      <span>{t('navigation.dashboard')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {isModuleEnabled('hr') && hasEmployee && !isModuleHiddenInSidebar('hr') && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActivePathname('/hr/me', pathname.pathname)}
                      onClick={() => setOpenMobile(!openMobile)}
                      asChild
                    >
                      <Link to="/hr/me">
                        <DynamicIcon name="CircleUser" className="h-4 w-4" />
                        <span>{t(`navigation.${titleCaseToCamelCase('My HR')}`)}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Module-specific navigation - only show when modules are loaded.
              Settings is pinned below everything else (including custom
              modules), so it's skipped in this loop and rendered again
              at the bottom of the content. */}
          {!modulesLoading &&
            moduleNavigationsData
              .filter((nav) => nav.title !== 'Settings')
              .slice()
              .sort((a, b) =>
                t(`navigation.${titleCaseToCamelCase(a.title)}`).localeCompare(
                  t(`navigation.${titleCaseToCamelCase(b.title)}`),
                  locale,
                ),
              )
              .map((nav) => {
                // Filter items based on module status, then add the datasets of the
                // modules in this group (same visibility rules as the module's own items).
                const extra = datasets.filter(
                  (d) =>
                    groupHostsModule(nav.items, d.moduleKey) &&
                    isModuleEnabled(d.moduleKey) &&
                    !isModuleHiddenInSidebar(d.moduleKey),
                )
                const filteredItems = mergeDatasetItems(
                  filterNavItems(nav.items),
                  extra,
                  (i) => t(`navigation.${titleCaseToCamelCase(i.title)}`),
                  locale,
                )

                // Skip rendering if no items are visible
                if (filteredItems.length === 0) {
                  return null
                }

                const title = t(`navigation.${titleCaseToCamelCase(nav.title)}`)

                const groupKey = nav.title
                const isCollapsed = isGroupCollapsed(groupKey)

                return (
                  <SidebarGroup key={nav.title}>
                    <Collapsible open={!isCollapsed} onOpenChange={() => toggleGroup(groupKey)}>
                      <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                        <span>{title}</span>
                        {isCollapsed ? (
                          <ChevronRight className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarGroupContent>
                          <SidebarMenu>
                            {filteredItems.map((item, index) =>
                              'taskId' in item ? (
                                <SidebarMenuItem key={`dataset:${item.taskId}`}>
                                  {renderDatasetItem(item)}
                                </SidebarMenuItem>
                              ) : (
                                <Fragment key={item.title}>
                                  <SidebarMenuItem>{renderMenuItem(item)}</SidebarMenuItem>
                                  {item.dividerAfter && index < filteredItems.length - 1 && (
                                    <SidebarSeparator />
                                  )}
                                </Fragment>
                              ),
                            )}
                          </SidebarMenu>
                        </SidebarGroupContent>
                      </CollapsibleContent>
                    </Collapsible>
                  </SidebarGroup>
                )
              })}

          {/* Custom modules (built by the tenant or installed from marketplace) */}
          {customModules && customModules.length > 0 && (
            <SidebarGroup>
              <Collapsible
                open={!isGroupCollapsed('Custom modules')}
                onOpenChange={() => toggleGroup('Custom modules')}
              >
                <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                  <span>{t('navigation.customModules')}</span>
                  {isGroupCollapsed('Custom modules') ? (
                    <ChevronRight className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {customModules
                        .filter((mod) => mod.status === 'active' || mod.status === 'paused')
                        .map((mod) => {
                          const to = `/m/${mod.key}`
                          const isActive = pathname.pathname.startsWith(to)
                          const navItems = navItemsByModule.get(mod.key) ?? []
                          const moduleIcon = mod.icon ? (
                            <DynamicIcon
                              name={mod.icon as Parameters<typeof DynamicIcon>[0]['name']}
                              className="h-4 w-4"
                            />
                          ) : (
                            <Box className="h-4 w-4" />
                          )

                          // Modules with custom nav items get a collapsible entry.
                          // Modules without nav items fall back to a simple link
                          // to the module home page.
                          if (navItems.length > 0) {
                            return (
                              <SidebarMenuItem key={mod.id}>
                                <Collapsible defaultOpen={isActive}>
                                  <CollapsibleTrigger asChild>
                                    <SidebarMenuButton className="w-full justify-between [&[data-state=open]>svg]:rotate-180">
                                      <span className="flex items-center gap-2">
                                        {moduleIcon}
                                        <span>{mod.name}</span>
                                      </span>
                                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                                    </SidebarMenuButton>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                                    <SidebarMenuSub>
                                      {/* Always show a link to the module home at the top */}
                                      <SidebarMenuItem>
                                        <SidebarMenuButton
                                          onClick={() => setOpenMobile(!openMobile)}
                                          asChild
                                        >
                                          <Link to="/m/$moduleKey" params={{ moduleKey: mod.key }}>
                                            <span className="text-muted-foreground">
                                              {t('navigation.overview')}
                                            </span>
                                          </Link>
                                        </SidebarMenuButton>
                                      </SidebarMenuItem>
                                      {navItems.map((item) => {
                                        // Only 'entity' nav items have a target route today.
                                        // 'custom_page' is reserved for future work and
                                        // silently skipped here.
                                        if (item.type !== 'entity' || !item.entityKey) return null
                                        return (
                                          <SidebarMenuItem key={item.id}>
                                            <SidebarMenuButton
                                              onClick={() => setOpenMobile(!openMobile)}
                                              asChild
                                            >
                                              <Link
                                                to="/m/$moduleKey/$entityKey"
                                                params={{
                                                  moduleKey: mod.key,
                                                  entityKey: item.entityKey,
                                                }}
                                              >
                                                {item.icon ? (
                                                  <DynamicIcon
                                                    name={
                                                      item.icon as Parameters<
                                                        typeof DynamicIcon
                                                      >[0]['name']
                                                    }
                                                    className="h-4 w-4"
                                                  />
                                                ) : null}
                                                <span>{item.label}</span>
                                              </Link>
                                            </SidebarMenuButton>
                                          </SidebarMenuItem>
                                        )
                                      })}
                                    </SidebarMenuSub>
                                  </CollapsibleContent>
                                </Collapsible>
                              </SidebarMenuItem>
                            )
                          }

                          return (
                            <SidebarMenuItem key={mod.id}>
                              <SidebarMenuButton
                                isActive={isActive}
                                onClick={() => setOpenMobile(!openMobile)}
                                asChild
                              >
                                <Link to="/m/$moduleKey" params={{ moduleKey: mod.key }}>
                                  {moduleIcon}
                                  <span className={mod.status === 'paused' ? 'opacity-60' : ''}>
                                    {mod.name}
                                  </span>
                                  {mod.status === 'paused' && (
                                    <Badge variant="outline" className="text-[9px] ml-auto">
                                      {t('navigation.paused')}
                                    </Badge>
                                  )}
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          )
                        })}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>
          )}

          {/* External (connected) services — one group per active connection.
              Each group lists the manifest's nav entries, routed through the
              /m/external/:connectionId path prefix so the renderer kicks in. */}
          {externalConnections &&
            externalConnections.length > 0 &&
            externalConnections
              .filter((conn) => conn.status === 'connected' && conn.enabled)
              .map((conn) => {
                const manifest = conn.manifestCachedJson as {
                  nav?: Array<{ title: string; path: string; icon?: string }>
                } | null
                const navItems = manifest?.nav ?? []
                const groupKey = `ext-${conn.id}`
                const isCollapsed = isGroupCollapsed(groupKey)
                return (
                  <SidebarGroup key={conn.id}>
                    <Collapsible open={!isCollapsed} onOpenChange={() => toggleGroup(groupKey)}>
                      <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                        <span>{conn.displayName}</span>
                        {isCollapsed ? (
                          <ChevronRight className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarGroupContent>
                          <SidebarMenu>
                            <SidebarMenuItem>
                              <SidebarMenuButton onClick={() => setOpenMobile(!openMobile)} asChild>
                                <Link
                                  to="/m/external/$connectionId"
                                  params={{ connectionId: conn.id }}
                                >
                                  <Box className="h-4 w-4" />
                                  <span className="text-muted-foreground">
                                    {t('navigation.overview')}
                                  </span>
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                            {navItems.map((item) => (
                              <SidebarMenuItem key={item.path}>
                                <SidebarMenuButton
                                  onClick={() => setOpenMobile(!openMobile)}
                                  asChild
                                >
                                  <a href={`/m/external/${conn.id}${item.path}`}>
                                    {item.icon ? (
                                      <DynamicIcon
                                        name={
                                          item.icon as Parameters<typeof DynamicIcon>[0]['name']
                                        }
                                        className="h-4 w-4"
                                      />
                                    ) : null}
                                    <span>{item.title}</span>
                                  </a>
                                </SidebarMenuButton>
                              </SidebarMenuItem>
                            ))}
                          </SidebarMenu>
                        </SidebarGroupContent>
                      </CollapsibleContent>
                    </Collapsible>
                  </SidebarGroup>
                )
              })}

          {/* Settings group — always last so it's anchored at the
              bottom of the sidebar under all module groups and any
              custom modules.

              Met een streep erboven, die de vastgepinde Instellingen scheidt van alles
              daarboven: de alfabetisch gesorteerde modulegroepen én de eigen modules van de
              tenant. Bewust niet aan de naam van de laatste groep opgehangen — die volgorde
              komt uit `localeCompare` op het VERTAALDE label, dus wie in het Nederlands
              "Voorraad" onderaan ziet staan ziet in het Engels "Sales", en de streep moet in
              elke taal op dezelfde plek blijven. Hij hangt daarom aan wat eronder komt. */}
          {!modulesLoading &&
            (() => {
              const settingsGroup = moduleNavigationsData.find((nav) => nav.title === 'Settings')
              if (!settingsGroup) return null
              const filteredItems = filterNavItems(settingsGroup.items)
              if (filteredItems.length === 0) return null
              // Single entry — rendered without a group header (a "Settings"
              // label above a "Settings" link would be redundant).
              return (
                <SidebarGroup>
                  <SidebarSeparator className="mb-2" />
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {filteredItems.map((item) => (
                        <SidebarMenuItem key={item.title}>{renderMenuItem(item)}</SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              )
            })()}
        </SidebarContent>
      </ScrollArea>
    </SidebarWrapper>
  )
}

/**
 * Branding + workspace switcher in the sidebar header. Always renders
 * the company logo (fallback icon) and trading name. When the user
 * has access to more than one workspace, the row also expands into a
 * switcher dropdown on click.
 */
function WorkspaceHeader({
  logoUrl,
  displayName,
  current,
  workspaces,
}: {
  logoUrl: string | null
  displayName: string
  current: { tenantId: string | null; name: string } | undefined
  workspaces: Array<{ tenantId: string; name: string; status: string }>
}) {
  const t = useTranslations()
  const [open, setOpen] = useState(false)
  const [switching, setSwitching] = useState(false)
  const hasSwitcher = workspaces.length > 1 && !!current

  async function switchWorkspace(_tenantId: string) {
    if (switching) return
    setSwitching(true)

    try {
      await fetch(`${BACKEND_URL}/api/v1/auth/sign-out`, {
        method: 'POST',
        credentials: 'include',
      })
      await fetch(`${BACKEND_URL}/api/v1/auth/clear-tenant-cookies`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch {
      // Best-effort — the sign-in page handles stale cookies
    }

    window.location.href = '/login'
  }

  const logo = logoUrl ? (
    <img src={logoUrl} alt="" className="h-6 w-6 flex-shrink-0 rounded object-cover" />
  ) : (
    <Building2 className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
  )

  const brandingRow = (
    <>
      {logo}
      <span className="flex-1 truncate font-medium">{displayName}</span>
      {hasSwitcher && <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />}
    </>
  )

  if (!hasSwitcher) {
    return (
      <div className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm">
        {brandingRow}
      </div>
    )
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors">
        {brandingRow}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 space-y-0.5">
        {workspaces.map((ws) => {
          const isCurrent = ws.tenantId === current?.tenantId
          return (
            <button
              key={ws.tenantId}
              type="button"
              disabled={isCurrent}
              onClick={() => switchWorkspace(ws.tenantId)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent disabled:opacity-60"
            >
              {isCurrent ? (
                <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
              ) : (
                <div className="h-3.5 w-3.5 flex-shrink-0" />
              )}
              <span className="flex-1 truncate">{ws.name}</span>
              {ws.status !== 'active' && (
                <span className="text-[10px] text-muted-foreground">
                  {ws.status === 'paused' ? t('navigation.paused') : t('navigation.deleting')}
                </span>
              )}
            </button>
          )
        })}
      </CollapsibleContent>
    </Collapsible>
  )
}
